/**
 * Client cho API v1 (/api/v1) — xem docs/api-v1/openapi.yaml.
 *
 * - Bóc envelope: trả { data, meta, message }, lỗi ném ApiError có `code` ổn định (TOKEN_EXPIRED, OTP_INVALID...)
 * - Gắn Authorization, X-Client-Platform, X-Client-Version
 * - 401 khi đang có token: thử refresh 1 lần (dùng chung cho mọi request đồng thời) rồi gọi lại;
 *   vẫn 401 thì xóa phiên và chuyển về /login (trừ khi onUnauthorized = 'throw')
 */
import { createApiUrl } from '../config/api';

export const API_V1_PREFIX = '/api/v1';
const CLIENT_VERSION = process.env.REACT_APP_VERSION || 'unknown';

export class ApiError extends Error {
  constructor({ status, code, message, details, meta, requestId }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.meta = meta || {}; // dữ liệu đi kèm lỗi, vd. time_left, use_google_login
    this.requestId = requestId;
  }
}

export const getStoredToken = () => localStorage.getItem('token') || localStorage.getItem('test_token');

export const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('test_token');
  localStorage.removeItem('user');
};

const redirectToLogin = () => {
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
};

// API phiên bản này đã bị tắt (410 API_VERSION_SUNSET): tải lại trang 1 lần để lấy bundle mới
const SUNSET_RELOAD_KEY = 'api_sunset_reloaded_at';
const reloadForNewVersion = () => {
  try {
    const last = Number(sessionStorage.getItem(SUNSET_RELOAD_KEY) || 0);
    if (Date.now() - last < 60 * 1000) return; // đã reload gần đây, tránh lặp vô hạn
    sessionStorage.setItem(SUNSET_RELOAD_KEY, String(Date.now()));
  } catch (_) {
    return;
  }
  window.location.reload();
};

const clientPlatform = () => {
  try {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    return standalone ? 'pwa' : 'web';
  } catch (_) {
    return 'web';
  }
};

/** URL đầy đủ của endpoint v1, bỏ qua tham số query rỗng */
export const apiUrl = (path, query) => {
  let url = createApiUrl(`${API_V1_PREFIX}${path}`);
  if (query) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      params.append(key, String(value));
    });
    const qs = params.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }
  return url;
};

const baseHeaders = () => ({
  Accept: 'application/json',
  'X-Client-Platform': clientPlatform(),
  'X-Client-Version': CLIENT_VERSION
});

let refreshPromise = null;

/**
 * Gia hạn access token (POST /auth/token/refresh). Nhiều request 401 cùng lúc chỉ gọi refresh 1 lần.
 * Thành công: lưu token mới và phát sự kiện 'tokenRefreshed' cho AuthContext.
 */
export const refreshAccessToken = (oldToken = getStoredToken()) => {
  if (!oldToken || oldToken.startsWith('test_token_')) return Promise.resolve(null);
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(apiUrl('/auth/token/refresh'), {
          method: 'POST',
          headers: { ...baseHeaders(), Authorization: `Bearer ${oldToken}` }
        });
        const json = await res.json().catch(() => null);
        const newToken = json && json.success && json.data && json.data.access_token;
        if (!newToken) return null;
        localStorage.setItem('token', newToken);
        if (json.data.user) {
          try { localStorage.setItem('user', JSON.stringify(json.data.user)); } catch (_) {}
        }
        window.dispatchEvent(new CustomEvent('tokenRefreshed', { detail: { token: newToken, user: json.data.user } }));
        return newToken;
      } catch (_) {
        return null;
      } finally {
        setTimeout(() => { refreshPromise = null; }, 0);
      }
    })();
  }
  return refreshPromise;
};

/**
 * Gọi API v1.
 * @param {string} path   vd. '/me/withdrawals'
 * @param {object} opts   method, query, body (object -> JSON, FormData giữ nguyên), headers, signal,
 *                        auth (mặc định true), onUnauthorized ('redirect' | 'throw')
 * @returns {Promise<{data:any, meta:object, message?:string}>}
 */
export const apiRequest = async (path, {
  method = 'GET',
  query,
  body,
  headers = {},
  signal,
  auth = true,
  onUnauthorized = 'redirect'
} = {}) => {
  const send = (token) => {
    const h = { ...baseHeaders(), ...headers };
    let payload = body;
    if (body !== undefined && !(body instanceof FormData)) {
      h['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
    if (auth && token) h.Authorization = `Bearer ${token}`;
    return fetch(apiUrl(path, query), { method, headers: h, body: payload, signal });
  };

  const token = auth ? getStoredToken() : null;
  let res = await send(token);

  if (res.status === 401 && token) {
    const newToken = await refreshAccessToken(token);
    if (newToken) res = await send(newToken);
    if (res.status === 401 && onUnauthorized === 'redirect') {
      clearSession();
      redirectToLogin();
    }
  }

  const json = res.status === 204 ? { success: true, data: null } : await res.json().catch(() => null);
  if (!res.ok || !json || json.success !== true) {
    const err = (json && json.error) || {};
    if (res.status === 410 && err.code === 'API_VERSION_SUNSET') reloadForNewVersion();
    throw new ApiError({
      status: res.status,
      code: err.code || (res.ok ? 'INVALID_RESPONSE' : `HTTP_${res.status}`),
      message: err.message || 'Có lỗi xảy ra, vui lòng thử lại',
      details: err.details,
      meta: err.meta,
      requestId: json && json.request_id
    });
  }
  return { data: json.data, meta: json.meta || {}, message: json.message };
};

/**
 * Đổi kết quả danh sách cursor của v1 ({ data, meta }) về dạng { success, data, pagination } —
 * định dạng mà cache localStorage (orders...) đang lưu, kể cả cache có sẵn từ bản app trước.
 */
export const toLegacyCursorPage = ({ data, meta = {} }) => ({
  success: true,
  data: Array.isArray(data) ? data : [],
  pagination: {
    limit: meta.limit,
    has_more: Boolean(meta.has_more),
    next_cursor: meta.next_cursor ?? null
  }
});

export const api = {
  get: (path, opts) => apiRequest(path, { ...opts, method: 'GET' }),
  post: (path, body, opts) => apiRequest(path, { ...opts, method: 'POST', body }),
  put: (path, body, opts) => apiRequest(path, { ...opts, method: 'PUT', body }),
  patch: (path, body, opts) => apiRequest(path, { ...opts, method: 'PATCH', body }),
  delete: (path, opts) => apiRequest(path, { ...opts, method: 'DELETE' })
};

export default api;
